// src/components/departments/DepartmentStaffList.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import AddStaffForm from './AddStaffForm';
import EditStaffForm from './EditStaffForm';

const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#06b6d4'
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

function DepartmentStaffList({ departmentId, staff, onStaffListChanged }) {
  const { t } = useTranslation('departments');
  const { currentUser, currentCompany } = useAuth(); // ARCHIVE: added currentCompany
  const [showAddStaffForm, setShowAddStaffForm] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [error, setError] = useState('');

  const handleRemoveStaff = async (staffId) => {
    if (!currentUser || !departmentId || !staffId) return;

    if (!window.confirm(t('confirmRemoveStaff'))) {
      return;
    }

    try {
      // ARCHIVE: was `currentUser.uid` unconditionally — same companyId bug
      // fixed in EditStaffForm.js / AddStaffForm.js / DepartmentDetailPage.js.
      const companyId = currentCompany?.id || currentUser.uid;

      const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
      await updateDoc(departmentRef, {
        staffIds: arrayRemove(staffId)
      });

      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      const staffSnap = await getDoc(staffRef);

      if (staffSnap.exists()) {
        const staffData = staffSnap.data();

        await updateDoc(staffRef, {
          departmentIds: arrayRemove(departmentId)
        });

        if (staffData.primaryDepartmentId === departmentId) {
          const updatedDepartmentIds = staffData.departmentIds.filter(id => id !== departmentId);
          await updateDoc(staffRef, {
            primaryDepartmentId: updatedDepartmentIds[0] || ''
          });
        }
      }

      if (onStaffListChanged) {
        onStaffListChanged();
      }

      setError('');
    } catch (err) {
      console.error('Error removing staff from department:', err);
      setError(t('errorRemoveStaff'));
    }
  };

  return (
    <div className="department-staff-list">

      {error && <div className="error-message">{error}</div>}

      <div className="staff-card">
        <div className="staff-card-header">
          <h2 className="staff-card-title">{t('staffMembers')}</h2>
          <button
            className="add-staff-btn"
            onClick={() => setShowAddStaffForm(true)}
          >
            {t('addStaff')}
          </button>
        </div>

        <div className="staff-card-divider" />

        {staff.length === 0 ? (
          <div className="empty-list">
            <p>{t('noStaffMembers')}</p>
          </div>
        ) : (
          <ul className="staff-list">
            {staff.map((staffMember) => (
              <li key={staffMember.id} className="staff-list-item">
                <div
                  className="staff-avatar"
                  style={{ backgroundColor: getAvatarColor(staffMember.name) }}
                >
                  {getInitials(staffMember.name)}
                </div>

                <div className="staff-info">
                  <span className="staff-name">{staffMember.name}</span>
                </div>

                <div className="staff-actions">
                  <button
                    className="edit-button"
                    onClick={() => setSelectedStaff(staffMember)}
                  >
                    {t('edit')}
                  </button>
                  <button
                    className="remove-button"
                    onClick={() => handleRemoveStaff(staffMember.id)}
                  >
                    {t('remove')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddStaffForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <AddStaffForm
              departmentId={departmentId}
              onStaffAdded={() => {
                setShowAddStaffForm(false);
                if (onStaffListChanged) {
                  onStaffListChanged();
                }
              }}
              onCancel={() => setShowAddStaffForm(false)}
            />
          </div>
        </div>
      )}

      {selectedStaff && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EditStaffForm
              staffMember={selectedStaff}
              onStaffUpdated={() => {
                setSelectedStaff(null);
                if (onStaffListChanged) {
                  onStaffListChanged();
                }
              }}
              onCancel={() => setSelectedStaff(null)}
            />
          </div>
        </div>
      )}

    </div>
  );
}

export default DepartmentStaffList;